import {createApi} from '@reduxjs/toolkit/query/react';
import {baseQuery} from '@store/baseQuery';
import {IToken} from '@store/types';
import {handleError, handleSuccess} from '@store/app.slice';
import {reSaveCredential} from '../../helpers/reSaveCredential';
import {IProfile, IPublicSettings} from './types';

interface IGetProfileResponse {
    user: IProfile
}

interface IGetPublicSettingsResponse {
    settings: IPublicSettings;
}

interface ITokenProps {
    token: IToken | null
}

interface IUpdateProps<T> extends ITokenProps {
    body: T
}

interface IInviteCompanyMemberResponse {
    message: string;
    email: string;
}

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const err = error as {
        data?: { message?: string; errors?: Record<string, string[]> };
        error?: { data?: { message?: string; errors?: Record<string, string[]> } };
    };
    const data = err?.data ?? err?.error?.data;

    if (!data) {
        return fallback;
    }

    const emailErrors = data.errors?.email;
    if (emailErrors?.[0]) {
        return emailErrors[0];
    }

    const inviteErrors = data.errors?.invite_emails;
    if (inviteErrors?.[0]) {
        return inviteErrors[0];
    }

    return data.message || fallback;
};

export const ProfileApi = createApi({
    reducerPath: 'profileApi',
    baseQuery: baseQuery(),
    tagTypes: ['Profile'],
    endpoints: build => ({
        getUserProfile: build.query<IProfile, any>({
            query: () => ({
                url: '/user'
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            providesTags: ['Profile'],
        }),
        getPublicSettings: build.query<IPublicSettings, void>({
            query: () => ({
                url: '/settings/public',
            }),
            transformResponse: (response: IGetPublicSettingsResponse) => response.settings,
        }),
        updateUserProfile: build.mutation<IProfile, IUpdateProps<Pick<IProfile, 'full_name' | 'region' | 'city' | 'phone' | 'company_name'>>>({
            query: ({body}) => ({
                url: '/user',
                method: 'POST',
                body,
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            async onQueryStarted({token, ...patch}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            () => {
                                return data;
                            })
                    );
                    dispatch(handleSuccess({message: 'Профиль сохранён'}));
                } catch (e: any) {
                    const status = e?.error?.status ?? e?.status;
                    if (status === 422) {
                        return;
                    }
                    dispatch(handleError({
                        message: getApiErrorMessage(e, 'Не удалось сохранить профиль'),
                        errors: {},
                    }));
                }
            },
        }),
        updateAvatar: build.mutation<IProfile, { id: number, token: IToken }>({
            query: ({id}) => ({
                url: '/user/avatar',
                method: 'POST',
                body: {
                    avatar: id
                },
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            async onQueryStarted({id, token, ...patch}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            () => {
                                return data;
                            })
                    );
                } catch (e:any) {
                    dispatch(handleError({message: e.response.message, errors:{}}));
                }
            },
        }),
        deleteAvatar: build.mutation<IProfile, { token: IToken }>({
            query: () => ({
                url: '/user/avatar',
                method: 'DELETE',
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            async onQueryStarted({token, ...patch}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            () => {
                                return data;
                            })
                    );
                } catch (e:any) {
                    dispatch(handleError({message: e.response.message, errors:{}}));
                }
            },
        }),
        leaveCompany: build.mutation<IProfile, { token: IToken }>({
            query: () => ({
                url: '/user/company/leave',
                method: 'POST',
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            async onQueryStarted({token}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            () => data,
                        ),
                    );
                    dispatch(handleSuccess({message: 'Вы покинули компанию'}));
                } catch (e: any) {
                    dispatch(handleError({
                        message: getApiErrorMessage(e, 'Не удалось покинуть компанию'),
                        errors: {},
                    }));
                }
            },
        }),
        detachCompanyMember: build.mutation<IProfile, { memberId: number; token: IToken }>({
            query: ({memberId}) => ({
                url: `/user/company-members/${memberId}`,
                method: 'DELETE',
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            async onQueryStarted({token}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            () => data,
                        ),
                    );
                    dispatch(handleSuccess({message: 'Пользователь отвязан от компании'}));
                } catch (e: any) {
                    dispatch(handleError({
                        message: getApiErrorMessage(e, 'Не удалось отвязать пользователя'),
                        errors: {},
                    }));
                }
            },
        }),
        inviteCompanyMember: build.mutation<IInviteCompanyMemberResponse, { email: string; token: IToken }>({
            query: ({email}) => ({
                url: '/user/company-members/invite',
                method: 'POST',
                body: {email},
            }),
        }),
        userContactsVerify: build.mutation<IProfile, { url: string, token: IToken, type: 'phone' | 'email' }>({
            query: ({url}) => ({
                url: url,
                method: 'GET',
            }),
            transformResponse: (response: IGetProfileResponse) => response.user,
            async onQueryStarted({url, token, type, ...patch}, {dispatch, queryFulfilled}) {
                try {
                    const {data} = await queryFulfilled;

                    await reSaveCredential({type, data});

                    dispatch(
                        ProfileApi.util.updateQueryData(
                            'getUserProfile',
                            {token},
                            () => {
                                return data;
                            })
                    );
                    dispatch(handleSuccess({message: 'Успешно верифицировано!'}));
                } catch (e:any) {
                    dispatch(handleError({message: e.response.message, errors:{}}));
                }
            },
        })
    }),
});

export const {
    useGetUserProfileQuery,
    useGetPublicSettingsQuery,
    useUpdateUserProfileMutation,
    useUpdateAvatarMutation,
    useDeleteAvatarMutation,
    useDetachCompanyMemberMutation,
    useLeaveCompanyMutation,
    useInviteCompanyMemberMutation,
    useUserContactsVerifyMutation
} = ProfileApi;
